import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, GripVertical, Clock, Repeat } from 'lucide-react';
import { calendarSettingsService, SessionType, CreateSessionTypeDto } from '../../services/calendarSettingsService';

const CATEGORY_LABELS = {
  FORMATION_CONTINUE: 'Formation Continue',
  FORMATION_INITIALE: 'Formation Initiale',
};

const RAMADAN_BEHAVIOR_LABELS = {
  KEEP_SAME: 'Garder la même durée',
  SHORTEN: 'Raccourcir (durée personnalisée)',
};

interface SessionTypesManagerProps {
  canManage?: boolean;
}

export const SessionTypesManager: React.FC<SessionTypesManagerProps> = ({ canManage = true }) => {
  const [sessionTypes, setSessionTypes] = useState<SessionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<SessionType | null>(null);
  const [formData, setFormData] = useState<CreateSessionTypeDto>({
    name: '',
    code: '',
    category: 'FORMATION_INITIALE',
    durationMinutes: 120,
    frequencyPerWeek: 1,
    ramadanBehavior: 'KEEP_SAME',
  });

  useEffect(() => {
    loadSessionTypes();
  }, []);

  const loadSessionTypes = async () => {
    try {
      setLoading(true);
      const data = await calendarSettingsService.getSessionTypes();
      setSessionTypes(data);
    } catch (error) {
      console.error('Failed to load session types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (type: SessionType) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      code: type.code,
      category: type.category,
      durationMinutes: type.durationMinutes,
      frequencyPerWeek: type.frequencyPerWeek,
      includesBreak: type.includesBreak,
      breakDuration: type.breakDuration,
      ramadanBehavior: type.ramadanBehavior,
      ramadanDurationMinutes: type.ramadanDurationMinutes,
      paidDurationMinutes: type.paidDurationMinutes ?? null,
      ramadanPaidDurationMinutes: type.ramadanPaidDurationMinutes ?? null,
      color: type.color,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce type de séance?')) return;

    try {
      await calendarSettingsService.deleteSessionType(id);
      await loadSessionTypes();
    } catch (error) {
      console.error('Failed to delete session type:', error);
    }
  };

  const [realignNotice, setRealignNotice] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setRealignNotice(null);
      if (editingType) {
        const result = await calendarSettingsService.updateSessionType(editingType.id, formData);
        // The paid duration changed: tell the user what happened to existing courses
        if (result.realignment) {
          const touched = result.realignment.filter((r) => !r.skippedReason);
          const removed = touched.reduce((s, r) => s + r.removed, 0);
          const added = touched.reduce((s, r) => s + r.added, 0);
          const conflicts = touched.flatMap((r) => r.conflictWarnings);
          setRealignNotice(
            `${touched.length} cours recalculé${touched.length > 1 ? 's' : ''} : ${removed} séance${removed > 1 ? 's' : ''} future${removed > 1 ? 's' : ''} remplacée${removed > 1 ? 's' : ''} par ${added}.` +
              (conflicts.length ? ` ⚠️ ${conflicts.length} conflit${conflicts.length > 1 ? 's' : ''} à vérifier : ${conflicts.slice(0, 3).join(' · ')}` : ''),
          );
        }
      } else {
        await calendarSettingsService.createSessionType(formData);
      }
      setShowForm(false);
      setEditingType(null);
      setFormData({
        name: '',
        code: '',
        category: 'FORMATION_INITIALE',
        durationMinutes: 120,
        frequencyPerWeek: 1,
        ramadanBehavior: 'KEEP_SAME',
      });
      await loadSessionTypes();
    } catch (error) {
      console.error('Failed to save session type:', error);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h${mins}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {realignNotice && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
          {realignNotice}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-semibold text-gray-900">Types de séance</h3>
          <p className="text-sm text-gray-500">Gérez les différents types de cours et leur durée</p>
        </div>
        {canManage && (
          <button
            onClick={() => {
              setEditingType(null);
              setFormData({
                name: '',
                code: '',
                category: 'FORMATION_INITIALE',
                durationMinutes: 120,
                frequencyPerWeek: 1,
                ramadanBehavior: 'KEEP_SAME',
              });
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={18} />
            Ajouter un type
          </button>
        )}
      </div>

      {/* Session Types List */}
      <div className="space-y-3">
        {sessionTypes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            Aucun type de séance configuré
          </div>
        ) : (
          sessionTypes.map((type) => (
            <div
              key={type.id}
              className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              <div className="cursor-move text-gray-400">
                <GripVertical size={20} />
              </div>

              {/* Color indicator */}
              <div
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: type.color || '#6366F1' }}
              ></div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">{type.name}</span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                    {type.code}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {formatDuration(type.durationMinutes)}
                    {type.paidDurationMinutes && type.paidDurationMinutes !== type.durationMinutes && (
                      <span className="ml-1 text-emerald-700">· payé {formatDuration(type.paidDurationMinutes)}</span>
                    )}
                    {type.ramadanBehavior === 'SHORTEN' && type.ramadanPaidDurationMinutes && (
                      <span className="ml-1 text-emerald-700">· Ramadan payé {formatDuration(type.ramadanPaidDurationMinutes)}</span>
                    )}
                    {type.ramadanBehavior === 'SHORTEN' && type.ramadanDurationMinutes && (
                      <span className="text-orange-600 ml-1">
                        → {formatDuration(type.ramadanDurationMinutes)} (Ramadan)
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-1">
                    <Repeat size={14} />
                    {type.frequencyPerWeek}x/semaine
                  </span>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                    {CATEGORY_LABELS[type.category]}
                  </span>
                </div>
              </div>

              {/* Actions - only shown if user can manage */}
              {canManage && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(type)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(type.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingType ? 'Modifier le type de séance' : 'Nouveau type de séance'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="ex: Cours 2h"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="ex: COURS_2H"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catégorie
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as 'FORMATION_CONTINUE' | 'FORMATION_INITIALE' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="FORMATION_INITIALE">Formation Initiale</option>
                  <option value="FORMATION_CONTINUE">Formation Continue</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Durée (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData.durationMinutes}
                    onChange={(e) => setFormData({ ...formData, durationMinutes: parseInt(e.target.value) })}
                    min="15"
                    max="480"
                    step="15"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fréquence/semaine
                  </label>
                  <input
                    type="number"
                    value={formData.frequencyPerWeek}
                    onChange={(e) => setFormData({ ...formData, frequencyPerWeek: parseInt(e.target.value) })}
                    min="1"
                    max="7"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* Durée rémunérée: what the professor is paid per session */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durée rémunérée (minutes)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={formData.paidDurationMinutes ?? ''}
                    onChange={(e) => setFormData({ ...formData, paidDurationMinutes: e.target.value ? parseInt(e.target.value) : null })}
                    min="15"
                    max="480"
                    step="15"
                    placeholder={`${formData.durationMinutes} (= durée réelle)`}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <span className="text-sm text-gray-500 whitespace-nowrap">
                    = {formatDuration(formData.paidDurationMinutes ?? formData.durationMinutes)} payée{formData.paidDurationMinutes && formData.paidDurationMinutes !== formData.durationMinutes ? ` pour ${formatDuration(formData.durationMinutes)} de cours` : ''}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Compte pour le volume horaire et la paie des vacataires. Modifier cette valeur recalcule les séances futures non pointées des cours concernés.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comportement Ramadan
                </label>
                <select
                  value={formData.ramadanBehavior}
                  onChange={(e) => {
                    const behavior = e.target.value as 'KEEP_SAME' | 'SHORTEN';
                    setFormData({
                      ...formData,
                      ramadanBehavior: behavior,
                      // Reset Ramadan durations when switching to KEEP_SAME
                      ramadanDurationMinutes: behavior === 'KEEP_SAME' ? undefined : formData.ramadanDurationMinutes,
                      ramadanPaidDurationMinutes: behavior === 'KEEP_SAME' ? null : formData.ramadanPaidDurationMinutes,
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="KEEP_SAME">{RAMADAN_BEHAVIOR_LABELS.KEEP_SAME}</option>
                  <option value="SHORTEN">{RAMADAN_BEHAVIOR_LABELS.SHORTEN}</option>
                </select>
              </div>

              {/* Durée pendant Ramadan - visible uniquement si SHORTEN */}
              {formData.ramadanBehavior === 'SHORTEN' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Durée pendant Ramadan (minutes)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      value={formData.ramadanDurationMinutes || ''}
                      onChange={(e) => setFormData({ ...formData, ramadanDurationMinutes: parseInt(e.target.value) || undefined })}
                      min="15"
                      max={formData.durationMinutes - 1}
                      step="15"
                      placeholder={`Max ${formData.durationMinutes - 1}`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                    <span className="text-sm text-gray-500">
                      (au lieu de {formData.durationMinutes} min)
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Ex: 120 → 90 minutes
                  </p>
                </div>
              )}

              {/* Durée rémunérée pendant Ramadan - visible uniquement si SHORTEN */}
              {formData.ramadanBehavior === 'SHORTEN' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Durée rémunérée pendant Ramadan (minutes)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      value={formData.ramadanPaidDurationMinutes ?? ''}
                      onChange={(e) => setFormData({ ...formData, ramadanPaidDurationMinutes: e.target.value ? parseInt(e.target.value) : null })}
                      min="15"
                      max="480"
                      step="15"
                      placeholder="Proportionnel si vide"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <span className="text-sm text-gray-500 whitespace-nowrap">
                      {formData.ramadanPaidDurationMinutes
                        ? `= ${formatDuration(formData.ramadanPaidDurationMinutes)} payée pour ${formatDuration(formData.ramadanDurationMinutes || 0)} de cours`
                        : `vide = au prorata de la durée rémunérée normale`}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Couleur
                </label>
                <input
                  type="color"
                  value={formData.color || '#6366F1'}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full h-10 px-1 py-1 border border-gray-300 rounded-lg cursor-pointer"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingType(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  {editingType ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
